import React, { useState, useEffect } from "react";
import Card from "react-bootstrap/Card";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";

import FormikContainer from "../../../components/form/FormikContainer.jsx";
import DeleteConfirmationModal from "../../../components/modal/DeleteConfirmationModal.jsx";
import genericTranslations from "../../../utils/genericTranslations.js";
import { selectUrlEntryById } from "../selectors";
import { fetchUrlEntryById, removeUrlEntry, saveUrlEntry } from "../thunks.js";

import UrlEntryForm from "./UrlEntryForm.jsx";

const messages = {
  noAudiences: {
    id: "modules.UrlEntry.components.UrlEntryEditor.noAudiences",
    description: "No audiences message",
    defaultMessage: "At least one group or user should be paired!",
  },
  type: {
    id: "modules.UrlEntry.components.UrlEntryEditor.type",
    description: "Type message",
    defaultMessage: "Type has to be 'Internal' or 'External'",
  },
  urlNotFound: {
    id: "modules.UrlEntry.components.UrlEntryEditor.urlNotFound",
    description: "URL not found message",
    defaultMessage: "URL not found",
  },
  deleteUrlEntry: {
    id: "modules.UrlEntry.components.UrlEntryEditor.delete",
    description: "URL not found message",
    defaultMessage: "Delete URL module",
  },
  deleteMessageConfirmation: {
    id: "modules.UrlEntry.components.UrlEntryEditor.deleteMessageConfirmation",
    description: "Delete URL message",
    defaultMessage: "Are you sure you want to delete this URL?",
  },
};

const UrlEntryEditor = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { identifier } = useParams();

  const [error, setError] = useState(undefined);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const urlEntry = useSelector((state) =>
    selectUrlEntryById(state, { identifier }),
  );

  const initialValues = React.useMemo(() => {
    if (!urlEntry) return {};
    return {
      ...urlEntry,
      audiences: (urlEntry.audiences ?? []).map((audience) => ({
        label: audience.displayName,
        value: {
          id: audience.principalId,
          resourceType: audience.principalType, // specific for react-select
        },
      })),
    };
  }, [urlEntry]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await dispatch(fetchUrlEntryById(identifier));
      } catch (err) {
        setError(err);
      }
    };

    if (identifier && !urlEntry) {
      fetchData();
    }
  }, [urlEntry, identifier, dispatch]);

  const handleSubmit = async (values, { setSubmitting }) => {
    setSubmitting(true);

    try {
      await dispatch(saveUrlEntry(values, urlEntry ? urlEntry.id : undefined));
      navigate("/url-entry", { replace: true });
    } catch (err) {
      setError(`Er ging iets fout bij het opslaan: ${err}`);
    } finally {
      setSubmitting(false);
    }
  };

  const validate = (values) => {
    const errors = {};
    const required = ["name", "url", "browsingType", "audiences"];
    required.forEach((k) => {
      if (!values[k]) {
        errors[k] = intl.formatMessage(genericTranslations.required);
      }
      if (
        k === "browsingType" &&
        !(values[k] === "Internal" || values[k] === "External")
      ) {
        errors[k] = intl.formatMessage(messages.type);
      }
    });
    if (!values.audiences || values.audiences.length === 0) {
      errors.audiences = intl.formatMessage(messages.noAudiences);
    }
    return errors;
  };

  const handleRemoveUrlEntry = async () => {
    try {
      await dispatch(removeUrlEntry(urlEntry.id));
      navigate("/url-entry", { replace: true });
    } catch (err) {
      setError(`Er ging iets fout bij het opslaan: ${err}`);
    }
  };

  if (error) {
    return (
      <strong>{intl.formatMessage(genericTranslations.errorMessage)} </strong>
    );
  }

  if (!urlEntry && identifier) {
    return (
      <strong>{intl.formatMessage(genericTranslations.nothingFound)}</strong>
    );
  }

  return (
    <Card>
      <Card.Header className="card-header-borderless" as="h3">
        {urlEntry
          ? intl.formatMessage(genericTranslations.edit).toLowerCase()
          : `URL ${intl.formatMessage(genericTranslations.add).toLowerCase()}`}
      </Card.Header>
      <Card.Body>
        <FormikContainer
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validate={validate}
          formComponent={UrlEntryForm}
          extraProps={{
            onClickDelete: () => {
              setShowDeleteModal(true);
            },
          }}
        />
        <DeleteConfirmationModal
          show={showDeleteModal}
          title={intl.formatMessage(messages.deleteUrlEntry)}
          bodyText={intl.formatMessage(messages.deleteMessageConfirmation)}
          confirmButtonText={intl.formatMessage(genericTranslations.delete)}
          cancelButtonText={intl.formatMessage(genericTranslations.close)}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleRemoveUrlEntry}
        />
      </Card.Body>
    </Card>
  );
};

export default UrlEntryEditor;
